/**
 * Cierre del baseline: dificultades principales y autoevaluación mínima.
 *
 * Es donde el baseline deja de ser una sesión más y se convierte en la semilla
 * del programa. Lo que se guarde acá decide qué va a priorizar el Día 4 y, más
 * adelante, la recomendación del Día 5.
 *
 * DOS REGLAS DURAS:
 *  - Solo dificultades REALMENTE observadas. Nada se crea automáticamente y
 *    nadie está obligado a llenar cinco casillas.
 *  - El área es OPCIONAL. La interfaz sugiere; nunca fuerza. Un elemento sin
 *    área es válido y simplemente no entra en la puntuación del Día 5.
 */

import { useState } from 'react';

import { AREAS, NOMBRE_AREA } from '../../domain/modelo/areas';
import { nuevoId, type Id } from '../../domain/modelo/ids';
import { KINDS, NOMBRE_KIND, type Kind } from '../../domain/modelo/banco';
import { NOMBRE_PRIORIDAD, PRIORIDADES } from '../../domain/modelo/prioridad';
import {
  AVISO_NO_INVENTAR,
  MAXIMO_DIFICULTADES,
  QUE_SON_LAS_DIFICULTADES,
} from '../../domain/reglas/baseline';
import type { DificultadRegistrada } from '../../domain/modelo/sesion';
import estilos from './CierreBaseline.module.css';

/**
 * El tipo canónico vive en `domain/modelo/sesion` porque el borrador se
 * PERSISTE: si solo existiera acá, recargar a mitad de escribirlo lo perdería.
 */
export type DificultadBorrador = DificultadRegistrada;

export function dificultadVacia(): DificultadBorrador {
  return {
    id: nuevoId(),
    contexto: '',
    kind: 'error',
    area: null,
    correccionTarget: '',
    prioridad: 'media',
  };
}

interface Props {
  dificultades: DificultadBorrador[];
  onCambiarDificultades: (valor: DificultadBorrador[]) => void;
}

export function CierreBaseline({ dificultades, onCambiarDificultades }: Props) {
  const [expandida, setExpandida] = useState<number | null>(0);

  function actualizar(id: Id, cambios: Partial<DificultadBorrador>) {
    onCambiarDificultades(dificultades.map((d) => (d.id === id ? { ...d, ...cambios } : d)));
  }

  function anadir() {
    onCambiarDificultades([...dificultades, dificultadVacia()]);
    setExpandida(dificultades.length);
  }

  function quitar(id: Id) {
    onCambiarDificultades(dificultades.filter((d) => d.id !== id));
    setExpandida(null);
  }

  return (
    <div className={estilos.cierre}>
      <section className={estilos.seccion}>
        <h2 className={estilos.titulo}>Dificultades principales</h2>
        <p className={estilos.explicacion}>{QUE_SON_LAS_DIFICULTADES}</p>
        <p className={estilos.aviso}>{AVISO_NO_INVENTAR}</p>

        {dificultades.map((dificultad, indice) => (
          <div key={dificultad.id} className={estilos.dificultad}>
            <button
              type="button"
              className={estilos.cabeceraDificultad}
              onClick={() => setExpandida(expandida === indice ? null : indice)}
              aria-expanded={expandida === indice}
            >
              <span className={estilos.numero}>{indice + 1}</span>
              <span className={estilos.resumen}>
                {dificultad.contexto.trim() || 'Sin describir todavía'}
              </span>
            </button>

            {expandida === indice ? (
              <div className={estilos.campos}>
                <label className={estilos.campo}>
                  <span className={estilos.etiqueta}>¿Qué te frenó?</span>
                  <textarea
                    className={estilos.texto}
                    rows={2}
                    value={dificultad.contexto}
                    onChange={(e) => actualizar(dificultad.id, { contexto: e.target.value })}
                    placeholder="por ejemplo: me quedé en blanco al explicar el problema del pedido"
                  />
                </label>

                <label className={estilos.campo}>
                  <span className={estilos.etiqueta}>Qué tipo de cosa es</span>
                  <select
                    className={estilos.select}
                    value={dificultad.kind}
                    onChange={(e) => actualizar(dificultad.id, { kind: e.target.value as Kind })}
                  >
                    {KINDS.map((k) => (
                      <option key={k} value={k}>
                        {NOMBRE_KIND[k]}
                      </option>
                    ))}
                  </select>
                </label>

                <label className={estilos.campo}>
                  <span className={estilos.etiqueta}>Área (opcional)</span>
                  <select
                    className={estilos.select}
                    value={dificultad.area ?? ''}
                    onChange={(e) =>
                      actualizar(dificultad.id, {
                        area: e.target.value === '' ? null : (e.target.value as never),
                      })
                    }
                  >
                    <option value="">Sin clasificar</option>
                    {AREAS.map((a) => (
                      <option key={a} value={a}>
                        {NOMBRE_AREA[a]}
                      </option>
                    ))}
                  </select>
                  <span className={estilos.ayuda}>
                    Si no lo tenés claro, dejalo sin clasificar. Es una opción válida:
                    simplemente no entra en la recomendación del Día 5.
                  </span>
                </label>

                <label className={estilos.campo}>
                  <span className={estilos.etiqueta}>Cómo se diría bien (opcional)</span>
                  <input
                    className={estilos.input}
                    value={dificultad.correccionTarget}
                    onChange={(e) =>
                      actualizar(dificultad.id, { correccionTarget: e.target.value })
                    }
                    placeholder="la expresión o el sonido que te faltó"
                  />
                </label>

                <fieldset className={estilos.campo}>
                  <legend className={estilos.etiqueta}>Prioridad</legend>
                  <div className={estilos.prioridades}>
                    {PRIORIDADES.map((p) => (
                      <label
                        key={p}
                        className={
                          dificultad.prioridad === p
                            ? `${estilos.prioridad} ${estilos.prioridadActiva}`
                            : estilos.prioridad
                        }
                      >
                        <input
                          type="radio"
                          name={`prioridad-${indice}`}
                          value={p}
                          checked={dificultad.prioridad === p}
                          onChange={() => actualizar(dificultad.id, { prioridad: p })}
                          className="sr-only"
                        />
                        {NOMBRE_PRIORIDAD[p]}
                      </label>
                    ))}
                  </div>
                </fieldset>

                <button
                  type="button"
                  className={estilos.quitar}
                  onClick={() => quitar(dificultad.id)}
                >
                  Quitar esta dificultad
                </button>
              </div>
            ) : null}
          </div>
        ))}

        {dificultades.length < MAXIMO_DIFICULTADES ? (
          <button type="button" className={estilos.anadir} onClick={anadir}>
            Añadir otra dificultad
          </button>
        ) : (
          <p className={estilos.ayuda}>
            {MAXIMO_DIFICULTADES} es el máximo. Trabajar veinte cosas a la vez no
            funciona.
          </p>
        )}

        {dificultades.length === 0 ? (
          <p className={estilos.ayuda}>
            Si de verdad no notaste ninguna, podés seguir sin guardar ninguna. Es
            preferible a inventar una.
          </p>
        ) : null}
      </section>

    </div>
  );
}
